<script setup>
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';

const props = defineProps({
  conversationInbox: {
    type: [String, Number],
    default: '',
  },
});

const emit = defineEmits(['inbox-change']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const inboxes = useMapGetter('inboxes/getInboxes');
const uiSettings = useMapGetter('getUISettings');
const currentAccountId = useMapGetter('getCurrentAccountId');

// Sort inboxes alphabetically
const sortedInboxes = computed(() => {
  return [...(inboxes.value || [])].sort((a, b) => a.name.localeCompare(b.name));
});

// Current selected inbox (from URL or saved preference)
const selectedInboxId = computed(() => {
  if (props.conversationInbox) {
    return Number(props.conversationInbox);
  }
  return null; // "All" selected
});

// Save last selected inbox to uiSettings
const saveLastSelectedInbox = (inboxId) => {
  store.dispatch('updateUISettings', {
    last_selected_inbox_id: inboxId,
  });
};

// Navigate to inbox or all conversations
const selectInbox = (inboxId) => {
  saveLastSelectedInbox(inboxId);
  
  if (inboxId === null) {
    // Navigate to all conversations
    router.push({
      name: 'home',
      params: { accountId: currentAccountId.value },
    });
  } else {
    // Navigate to specific inbox
    router.push({
      name: 'inbox_dashboard',
      params: {
        accountId: currentAccountId.value,
        inbox_id: inboxId,
      },
    });
  }
  
  emit('inbox-change', inboxId);
};

// Get evolution status for ViperConnect badge
const getEvolutionStatus = (inbox) => {
  if (inbox.channel_type !== 'Channel::Api') return null;
  return inbox.additional_attributes?.evolution_status;
};

// On mount, restore last selected inbox if on home route
onMounted(() => {
  const lastSelectedInboxId = uiSettings.value?.last_selected_inbox_id;
  
  // Only auto-select if we're on home route and have a saved preference
  if (route.name === 'home' && lastSelectedInboxId && !props.conversationInbox) {
    // Check if the saved inbox still exists
    const inboxExists = sortedInboxes.value.some(inbox => inbox.id === lastSelectedInboxId);
    if (inboxExists) {
      selectInbox(lastSelectedInboxId);
    }
  }
});
</script>

<template>
  <div class="flex items-center gap-1 px-3 py-2 overflow-x-auto border-b border-n-weak bg-n-solid-1">
    <!-- All Conversations Tab -->
    <button
      class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all whitespace-nowrap flex-shrink-0"
      :class="selectedInboxId === null
        ? 'bg-n-brand text-white shadow-sm'
        : 'bg-n-slate-100 dark:bg-n-solid-2 text-n-slate-11 hover:bg-n-slate-200 dark:hover:bg-n-solid-3'"
      :style="selectedInboxId === null ? '' : 'border: 1px solid #e2e8f0;'"
      @click="selectInbox(null)"
    >
      <span class="i-lucide-layers size-3.5" />
      {{ t('CHAT_LIST.INBOX_TABS.ALL') }}
    </button>

    <!-- Inbox Tabs -->
    <button
      v-for="inbox in sortedInboxes"
      :key="inbox.id"
      class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all whitespace-nowrap flex-shrink-0"
      :class="selectedInboxId === inbox.id
        ? 'bg-n-brand text-white shadow-sm'
        : 'bg-n-slate-100 dark:bg-n-solid-2 text-n-slate-11 hover:bg-n-slate-200 dark:hover:bg-n-solid-3'"
      :style="selectedInboxId === inbox.id ? '' : 'border: 1px solid #e2e8f0;'"
      @click="selectInbox(inbox.id)"
    >
      <!-- Channel Icon -->
      <span class="size-4 grid place-content-center rounded-full bg-n-alpha-2 flex-shrink-0"
        :class="{ '!bg-white/20': selectedInboxId === inbox.id }">
        <ChannelIcon :inbox="inbox" class="size-2.5" />
      </span>
      
      <!-- Inbox Name -->
      <span class="max-w-[100px] truncate">{{ inbox.name }}</span>
      
      <!-- ViperConnect Status Badge -->
      <span
        v-if="getEvolutionStatus(inbox)"
        class="size-2 rounded-full flex-shrink-0"
        :class="{
          'bg-green-400': getEvolutionStatus(inbox) === 'connected',
          'bg-red-400': getEvolutionStatus(inbox) === 'disconnected',
          'bg-yellow-400': getEvolutionStatus(inbox) === 'qr_pending',
        }"
        :title="getEvolutionStatus(inbox) === 'connected' 
          ? t('SIDEBAR.VIPER_CONNECT_CONNECTED')
          : getEvolutionStatus(inbox) === 'disconnected'
            ? t('SIDEBAR.VIPER_CONNECT_DISCONNECTED')
            : t('SIDEBAR.VIPER_CONNECT_PENDING')"
      />
    </button>
  </div>
</template>

